import { useEffect, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Typeahead, STAFF_ROLES } from "@/components/Typeahead";
import { toast } from "sonner";
import { Plus, Users, Trash2, Star } from "lucide-react";

export default function CareTeamTab({ clientId, isReadOnly, onChange }) {
  const { currentOrgId, currentRole } = useAuth();
  const [team, setTeam] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", role_label: "Care Coordinator", is_primary: false, notes: "" });
  const canAssign = ["org_admin", "intake_coordinator", "staff"].includes(currentRole);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("care_team")
      .select("*, profiles:user_id(id, full_name, email)")
      .eq("client_id", clientId);
    setTeam(data || []);
    setLoading(false);
  };

  const loadStaff = async () => {
    // Two-step to avoid PostgREST FK-embed edge case where profiles!inner may not
    // resolve reliably. This also lets us guarantee we always show every active
    // member (including the caller themselves).
    const { data: members } = await supabase
      .from("organization_members")
      .select("user_id, role")
      .eq("organization_id", currentOrgId)
      .eq("status", "active");
    const ids = (members || []).map((m) => m.user_id);
    if (ids.length === 0) { setStaff([]); return; }
    const { data: profs } = await supabase.from("profiles").select("id, full_name").in("id", ids);
    const byId = Object.fromEntries((profs || []).map((p) => [p.id, p]));
    setStaff((members || []).map((m) => ({
      user_id: m.user_id,
      role: m.role,
      profiles: {
        id: m.user_id,
        full_name: byId[m.user_id]?.full_name || "Team member",
        email: "",
      },
    })));
  };

  useEffect(() => { if (clientId && currentOrgId) { load(); loadStaff(); } /* eslint-disable-next-line */ }, [clientId, currentOrgId]);

  const availableStaff = staff.filter((s) => !team.some((t) => t.user_id === s.user_id));

  const add = async () => {
    if (!form.user_id) return toast.error("Select a staff member");
    try {
      await apiFetch("/care-team", { method: "POST",
        body: JSON.stringify({ ...form, organization_id: currentOrgId, client_id: clientId }) });
      toast.success("Added to care team");
      setForm({ user_id: "", role_label: "primary", is_primary: false, notes: "" });
      setOpen(false); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove ${entry.profiles?.full_name || "this member"} from the care team?`)) return;
    try {
      await apiFetch(`/care-team/${entry.id}`, { method: "DELETE" });
      toast.success("Removed"); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="text-center py-12 text-sm text-[#57534E]">Loading care team…</div>;

  return (
    <div className="space-y-4" data-testid="careteam-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">Care Team</h3>
          <p className="text-xs text-[#57534E]">The staff members responsible for this client.</p>
        </div>
        {!isReadOnly && canAssign && availableStaff.length > 0 && (
          <Button data-testid="add-team-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            <Plus size={14} className="mr-1.5" /> Assign staff
          </Button>
        )}
      </div>

      {team.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]">
          <Users size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium">No team members assigned</p>
          <p className="text-xs text-[#57534E] mt-1">Assign staff to this client to define responsibility.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {team.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-4 flex items-start justify-between gap-3" data-testid={`team-${t.id}`}>
              <div className="flex gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-2xl bg-[#FDE9D8] text-[#E8712C] flex items-center justify-center font-semibold flex-shrink-0">
                  {(t.profiles?.full_name || t.profiles?.email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="text-sm font-medium truncate">{t.profiles?.full_name || t.profiles?.email}</div>
                    {t.is_primary && <Star size={12} className="text-[#E8712C] fill-[#E8712C]" />}
                  </div>
                  <div className="text-xs text-[#57534E] capitalize">{t.role_label}</div>
                  {t.notes && <div className="text-xs text-[#57534E] mt-1 italic">{t.notes}</div>}
                </div>
              </div>
              {!isReadOnly && (
                <Button size="sm" variant="ghost" data-testid={`del-team-${t.id}`} onClick={() => remove(t)} className="text-red-600"><Trash2 size={13} /></Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm({ user_id: "", role_label: "Care Coordinator", is_primary: false, notes: "" }); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Assign staff to client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team member *</Label>
              <div className="mt-1.5">
                <Typeahead testid="team-staff-typeahead"
                  options={availableStaff.map((s) => ({
                    value: s.user_id,
                    label: s.profiles.full_name || s.profiles.email,
                    sub: [s.profiles.email, s.role].filter(Boolean).join(" · "),
                  }))}
                  value={form.user_id}
                  onChange={(v) => setForm({ ...form, user_id: v })}
                  placeholder="Search team members…" />
              </div>
              {availableStaff.length === 0 && (
                <p className="text-xs text-[#A8A29E] mt-1">No more team members to assign — everyone is already on this client&apos;s care team.</p>
              )}
            </div>
            <div>
              <Label>Role on this case *</Label>
              <div className="mt-1.5">
                <Typeahead testid="team-role-typeahead"
                  options={STAFF_ROLES.map((r) => ({ value: r, label: r }))}
                  value={form.role_label}
                  onChange={(v) => setForm({ ...form, role_label: v || "Care Coordinator" })}
                  onCreate={(custom) => setForm({ ...form, role_label: custom })}
                  createLabel="Use custom role"
                  placeholder="Search or type a role…" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({...form, is_primary: e.target.checked})} className="w-4 h-4 accent-[#E8712C]" data-testid="team-primary" />
              Mark as primary point of contact
            </label>
            <div><Label>Notes (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" placeholder="Any context about this assignment" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-team-btn" onClick={add} disabled={!form.user_id} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] disabled:opacity-50">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
